import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { documentApi, type DocumentMeta } from '../../api';
import { fetchAuthorisedBlob } from '../../api/client';

// In-app preview for matter documents. The previous version pointed an
// <img>/<iframe> at the auth-required proxy stream — but browsers don't
// send custom auth headers from a media tag, so every preview returned 401.
//
// This version always fetches the bytes via authenticated fetch() and renders
// from a blob URL:
//
//   - application/pdf            → <iframe src={blobUrl}>
//   - image/*                    → <img src={blobUrl}>
//   - text/* / json / xml        → response.text() in a <pre>
//   - Excel / CSV                → SheetJS parses → renders as a table with tab strip
//   - everything else            → friendly fallback + Download button
//
// Blob URLs are revoked on cleanup so we don't leak memory if the user
// previews many docs in a session.

type Props = {
  document: DocumentMeta;
  onClose: () => void;
};

type PreviewKind = 'pdf' | 'image' | 'text' | 'spreadsheet' | 'other';

type SheetState = {
  names: string[];
  /** sheetName → 2D array of cell strings (already truncated for display). */
  data: Record<string, string[][]>;
  truncated: boolean;
};

const TEXT_MAX_BYTES = 200 * 1024;
const SHEET_MAX_ROWS = 500;
const SHEET_MAX_COLS = 50;

export default function DocumentPreviewModal({ document: doc, onClose }: Props) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textBody, setTextBody] = useState<string | null>(null);
  const [sheets, setSheets] = useState<SheetState | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Keep a ref so the cleanup function can revoke the most recent URL even if
  // state has moved on after the component unmounts.
  const lastBlobUrl = useRef<string | null>(null);

  const kind = useMemo(() => previewKind(doc.mime_type, doc.file_name), [doc.mime_type, doc.file_name]);

  // Close on Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fetch the bytes once. Always uses authenticated fetch → blob → URL,
  // sidestepping the no-auth-on-img / no-auth-on-iframe limitation.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    setTextBody(null);
    setSheets(null);

    const resolvePath = async (): Promise<string> => {
      if (doc.object_id && /^\d+$/.test(String(doc.object_id))) {
        return `/files/${doc.object_id}/download`;
      }
      // Legacy: use the presigned-URL endpoint. That URL doesn't require auth,
      // so fetchAuthorisedBlob still works (the auth header is ignored by Stratus).
      const r = await documentApi.downloadUrl(doc.ROWID);
      return r.download_url;
    };

    (async () => {
      try {
        const path = await resolvePath();
        const blob = await fetchAuthorisedBlob(path);
        if (cancelled) return;
        // Force the blob's type to match the document's stored MIME. The
        // server sometimes returns `application/octet-stream` (especially via
        // presigned Stratus URLs), and browsers refuse to render <iframe>/<embed>
        // unless the blob's content-type is the expected media type.
        const typedMime = pickBlobMime(doc.mime_type, doc.file_name, blob.type);
        const typed = typedMime && typedMime !== blob.type
          ? new Blob([blob], { type: typedMime })
          : blob;
        const url = URL.createObjectURL(typed);
        lastBlobUrl.current = url;
        setBlobUrl(url);

        // Format-specific post-processing.
        if (kind === 'text') {
          const slice = blob.slice(0, TEXT_MAX_BYTES);
          const text = await slice.text();
          if (!cancelled) setTextBody(text);
        } else if (kind === 'spreadsheet') {
          const buf = await blob.arrayBuffer();
          if (cancelled) return;
          const wb = XLSX.read(buf, { type: 'array' });
          const out: Record<string, string[][]> = {};
          let truncated = false;
          for (const name of wb.SheetNames) {
            const ws = wb.Sheets[name];
            const aoa = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, defval: '', blankrows: false, raw: false });
            // Truncate to keep DOM size sane on huge sheets.
            const sliced = aoa.slice(0, SHEET_MAX_ROWS).map((row) => row.slice(0, SHEET_MAX_COLS).map(cellToString));
            if (aoa.length > SHEET_MAX_ROWS || (aoa[0] && aoa[0].length > SHEET_MAX_COLS)) truncated = true;
            out[name] = sliced;
          }
          if (cancelled) return;
          setSheets({ names: wb.SheetNames, data: out, truncated });
          setActiveSheet(wb.SheetNames[0] || '');
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message || 'Could not load preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (lastBlobUrl.current) {
        URL.revokeObjectURL(lastBlobUrl.current);
        lastBlobUrl.current = null;
      }
    };
  }, [doc.ROWID, doc.object_id, doc.mime_type, kind]);

  // Download button: use the same blob (no extra network round-trip).
  const onDownload = () => {
    if (!blobUrl) return;
    const a = window.document.createElement('a');
    a.href = blobUrl;
    a.download = doc.file_name || 'document';
    a.click();
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modalShell} onClick={(e) => e.stopPropagation()}>
        <header style={headerBar}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {doc.title}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {doc.file_name} · {doc.mime_type} · v{doc.version}
            </div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button onClick={onDownload} disabled={!blobUrl} style={{ padding: '6px 12px', fontSize: 13 }}>
              Download
            </button>
            <button onClick={onClose} style={{ padding: '6px 12px', fontSize: 13 }}>Close</button>
          </div>
        </header>

        <div style={body}>
          {loading && (
            <div className="muted" style={{ padding: 32, textAlign: 'center' }}>Loading preview…</div>
          )}
          {error && !loading && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-danger)' }}>
              Couldn't load the preview: {error}
            </div>
          )}

          {!loading && !error && blobUrl && kind === 'pdf' && (
            // <object> is more reliable than <iframe> for PDF blob URLs across
            // Chrome / Firefox / Safari — Chrome's PDF viewer in particular
            // sometimes refuses iframes pointing at blob:. <object> fall-back
            // to an inner <iframe> + a download prompt covers the long tail.
            <object
              data={`${blobUrl}#view=FitH&toolbar=1`}
              type="application/pdf"
              style={{ width: '100%', height: '100%', background: '#fff' }}
            >
              <iframe
                src={blobUrl}
                title={doc.title}
                style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              />
            </object>
          )}

          {!loading && !error && blobUrl && kind === 'image' && (
            <div style={imageWrap}>
              <img
                src={blobUrl}
                alt={doc.title}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
              />
            </div>
          )}

          {!loading && !error && kind === 'text' && (
            <pre style={textBlock}>
              {textBody || '(empty)'}
            </pre>
          )}

          {!loading && !error && kind === 'spreadsheet' && sheets && (
            <SheetView sheets={sheets} active={activeSheet} onSetActive={setActiveSheet} />
          )}

          {!loading && !error && kind === 'other' && (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
              <div style={{ fontSize: 14, marginBottom: 4 }}>
                No inline preview for <code>{doc.mime_type}</code>.
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                Click <b>Download</b> above to open the file in its native app.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spreadsheet renderer — tab strip + scrollable grid.
// ---------------------------------------------------------------------------
function SheetView({
  sheets, active, onSetActive,
}: {
  sheets: SheetState;
  active: string;
  onSetActive: (n: string) => void;
}) {
  const data = sheets.data[active] || [];
  const headerRow = data[0] || [];
  const bodyRows = data.slice(1);

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tab strip — only when multiple sheets. */}
      {sheets.names.length > 1 && (
        <div style={{
          display: 'flex', gap: 4, padding: '6px 12px',
          background: 'var(--color-surface-2)',
          borderBottom: '1px solid var(--color-border)',
          overflowX: 'auto',
          flexShrink: 0,
        }}>
          {sheets.names.map((n) => (
            <button
              key={n}
              onClick={() => onSetActive(n)}
              style={{
                padding: '4px 12px',
                fontSize: 12,
                border: '1px solid var(--color-border)',
                borderRadius: 4,
                background: n === active ? 'var(--color-accent)' : 'var(--input-bg)',
                color: n === active ? '#1A1305' : 'var(--color-text)',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {/* Truncation notice. */}
      {sheets.truncated && (
        <div className="muted" style={{
          padding: '6px 12px', fontSize: 11,
          background: 'rgba(201,163,74,0.08)',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}>
          Large sheet — preview is capped at {SHEET_MAX_ROWS} rows × {SHEET_MAX_COLS} columns. Download to see the full file.
        </div>
      )}

      {/* Grid. */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#fff' }}>
        {data.length === 0 ? (
          <div className="muted" style={{ padding: 32, textAlign: 'center' }}>Sheet is empty.</div>
        ) : (
          <table style={sheetTable}>
            <thead>
              <tr>
                <th style={sheetCornerCell}></th>
                {headerRow.map((_, ci) => (
                  <th key={ci} style={sheetColLetterCell}>{colLetter(ci)}</th>
                ))}
              </tr>
              <tr style={{ background: 'var(--color-surface-2)' }}>
                <th style={sheetCornerCell}>1</th>
                {headerRow.map((cell, ci) => (
                  <th key={ci} style={sheetHeaderCell}>{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri}>
                  <th style={sheetRowNumCell}>{ri + 2}</th>
                  {row.map((cell, ci) => (
                    <td key={ci} style={sheetCell}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function previewKind(mime: string | null | undefined, filename: string | null | undefined): PreviewKind {
  const m = (mime || '').toLowerCase();
  const ext = ((filename || '').match(/\.([a-z0-9]{1,5})$/i)?.[1] || '').toLowerCase();
  if (m === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (m.startsWith('image/')) return 'image';
  if (isSpreadsheet(m, ext)) return 'spreadsheet';
  if (isText(m, ext)) return 'text';
  return 'other';
}

function isSpreadsheet(mime: string, ext: string): boolean {
  return mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      || mime === 'application/vnd.ms-excel'
      || mime === 'text/csv'
      || ext === 'xlsx' || ext === 'xls' || ext === 'csv';
}

function isText(mime: string, ext: string): boolean {
  return mime.startsWith('text/')
      || mime === 'application/json'
      || mime === 'application/xml'
      || mime === 'application/javascript'
      || ['txt', 'json', 'xml', 'md', 'log', 'js', 'ts', 'tsx', 'css', 'html'].includes(ext);
}

// Decide what MIME to put on the Blob before we hand it to the renderer.
// The document row's stored mime_type is the most trustworthy source. If it's
// missing or generic, derive from the filename extension. Last resort: trust
// what the server returned.
function pickBlobMime(docMime: string | null | undefined, filename: string | null | undefined, fallback: string): string {
  const trim = (s: string | null | undefined) => (s || '').trim().toLowerCase();
  const docM = trim(docMime);
  if (docM && docM !== 'application/octet-stream') return docM;
  const ext = ((filename || '').match(/\.([a-z0-9]{1,5})$/i)?.[1] || '').toLowerCase();
  switch (ext) {
    case 'pdf':  return 'application/pdf';
    case 'png':  return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'gif':  return 'image/gif';
    case 'webp': return 'image/webp';
    case 'svg':  return 'image/svg+xml';
    case 'heic': return 'image/heic';
    case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'xls':  return 'application/vnd.ms-excel';
    case 'csv':  return 'text/csv';
    case 'txt':  return 'text/plain';
    case 'json': return 'application/json';
    case 'xml':  return 'application/xml';
    case 'md':   return 'text/markdown';
  }
  return fallback || 'application/octet-stream';
}

function cellToString(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Excel-style column letters: A, B, …, Z, AA, AB, …
function colLetter(i: number): string {
  let s = '';
  let n = i;
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 200,
  padding: 24,
};

const modalShell: React.CSSProperties = {
  width: 'min(1200px, 96vw)',
  height: 'min(840px, 92vh)',
  display: 'flex', flexDirection: 'column',
  padding: 0,
  overflow: 'hidden',
};

const headerBar: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 16px',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2)',
};

const body: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  overflow: 'hidden',
};

const imageWrap: React.CSSProperties = {
  width: '100%', height: '100%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-surface-2)',
  overflow: 'auto',
  padding: 12,
};

const textBlock: React.CSSProperties = {
  margin: 0, padding: 18,
  fontSize: 12, lineHeight: 1.45,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  background: 'var(--color-surface-2)',
  height: '100%', width: '100%', overflow: 'auto',
};

const sheetTable: React.CSSProperties = {
  borderCollapse: 'collapse',
  fontSize: 12,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  background: '#fff',
};

const sheetCornerCell: React.CSSProperties = {
  position: 'sticky', top: 0, left: 0, zIndex: 3,
  background: '#e8ecf2', color: '#5e6b7f',
  border: '1px solid #c8d1de',
  padding: '4px 8px', textAlign: 'center',
  minWidth: 40,
};

const sheetColLetterCell: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 2,
  background: '#e8ecf2', color: '#5e6b7f',
  border: '1px solid #c8d1de',
  padding: '4px 8px', textAlign: 'center',
  fontWeight: 600,
  minWidth: 80,
};

const sheetRowNumCell: React.CSSProperties = {
  position: 'sticky', left: 0, zIndex: 1,
  background: '#e8ecf2', color: '#5e6b7f',
  border: '1px solid #c8d1de',
  padding: '4px 8px', textAlign: 'right',
  fontWeight: 500,
  minWidth: 40,
};

const sheetHeaderCell: React.CSSProperties = {
  border: '1px solid #c8d1de',
  padding: '4px 8px',
  background: '#f7f9fc',
  fontWeight: 600,
  color: '#1A1A1A',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const sheetCell: React.CSSProperties = {
  border: '1px solid #e3e8f0',
  padding: '4px 8px',
  color: '#1A1A1A',
  whiteSpace: 'nowrap',
  background: '#fff',
};
